import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  TemplateRef,
  ViewChild,
  ElementRef,
  ViewChildren,
  QueryList,
  ChangeDetectionStrategy,
} from "@angular/core";
import { trigger, transition, animate, style } from "@angular/animations";
import { UiService } from "../../services/ui.service";
import { KeyCodes } from "../../models/key-codes.model";
import { TranslateModule } from "@ngx-translate/core";
import { CommonModule } from "@angular/common";
import { DataBoxComponent } from "./data-box.component";
import { CheckboxWrapComponent } from "./checkbox-wrap.component";

@Component({
  selector: "checkbox-list",
  standalone: true,
  imports: [
    TranslateModule,
    CommonModule,
    DataBoxComponent,
    CheckboxWrapComponent,
  ],
  animations: [
    trigger("slideAway", [
      transition(":enter", [
        style({ transform: "translateY(0%)" }),
        animate("200ms ease-out", style({ transform: "translateY(0%)" })),
      ]),
      transition(":leave", [
        style({ transform: "translateX(0%)" }),
        animate(
          "400ms cubic-bezier(.53,-0.29,.77,.58)",
          style({ transform: "translateX(100%)" })
        ),
      ]),
    ]),
  ],

  template: `
    <div class="l-align-y">
      <h3 *ngIf="title" class="l-cell-spread-x t-title--small">
        {{ title | translate }}
      </h3>
      <ng-container *ngTemplateOutlet="afterTitele"></ng-container>
    </div>

    <ng-container *ngIf="pagination && paginationTop">
      <ng-container *ngTemplateOutlet="paginationListTemplate"></ng-container>
      <div class="t-row--small"></div>
    </ng-container>

    <ng-container *ngIf="displayAs === 'list'">
      <ng-container *ngTemplateOutlet="list"></ng-container>
    </ng-container>

    <ng-container *ngIf="displayAs === 'buttons'">
      <ng-container *ngTemplateOutlet="buttons"></ng-container>
    </ng-container>

    <ng-container *ngIf="displayAs === 'cards'">
      <ng-container *ngTemplateOutlet="cards"></ng-container>
    </ng-container>

    <ng-container *ngIf="pagination && !paginationTop">
      <div class="t-row--small"></div>
      <ng-container *ngTemplateOutlet="paginationListTemplate"></ng-container>
    </ng-container>

    <ng-template #list>
      <ul #clickableListItems class="l-cell-spread-x t-addRemove">
        <li
          *ngFor="let row of displayRows"
          #rows
          [attr.tabindex]="rowClickCallback ? '0' : ''"
          class="l-flex t-addRemove-item"
          [attr.role]="rowClickCallback ? 'button' : ''"
          (click)="rowClickCallbackWrapper(row, $event)"
        >
          <ng-container
            *ngTemplateOutlet="checkbox; context: { row: row }"
          ></ng-container>

          <ng-container
            *ngTemplateOutlet="listItemBody; context: { row: row }"
          ></ng-container>
        </li>
      </ul>
    </ng-template>

    <ng-template #listItemBody let-row="row">
      <ng-container *ngIf="row">
        <span *ngIf="beforeItem" class="cell-fit">
          <ng-container
            *ngTemplateOutlet="beforeItem; context: { row: row }"
          ></ng-container>
        </span>
        <span
          class="t-addRemove-prop"
          *ngFor="let prop of props"
          [ngClass]="'clItem-' + prop"
        >
          <ng-container *ngIf="isImage(row[prop])">
            <ng-container
              *ngTemplateOutlet="image; context: { cell: row[prop] }"
            ></ng-container>
          </ng-container>
          <ng-container *ngIf="!isImage(row[prop])">
            {{ row[prop] }}
          </ng-container>
        </span>

        <span class="cell-100 l-ta-end l-nowrap" *ngIf="idType">
          ({{ row[idType] }} )
        </span>
      </ng-container>
    </ng-template>

    <ng-template #buttons>
      <ul class="norm-ul l-flex-wrap t-buttonSet">
        <li
          *ngFor="let row of displayRows"
          class="t-buttonSet-child t-button t-button--small bg-white"
        >
          <span *ngIf="!removeUnselect && !checkList" class="ui-cell">
            <button
              class="btn-unselect"
              (click)="unSelect(row, $event)"
              [attr.aria-label]="
                'srpstoreapp.general.cancelSelection' | translate
              "
            ></button>
          </span>

          <span
            class="t-addRemove-prop"
            *ngFor="let prop of props"
            [ngClass]="'clItem-' + prop"
          >
            {{ row[prop] }}
          </span>
        </li>
      </ul>
    </ng-template>

    <ng-template #cards>
      <ul class="norm-ul g-cards">
        <li class="g-cards-item" *ngFor="let row of displayRows">
          <div
            [ngClass]="{ 't-widget t-pad--small t-row--0': !isUnWrapped }"
            class="h-full l-align-x l-align-y"
          >
            <ng-container
              *ngTemplateOutlet="checkbox; context: { row: row }"
            ></ng-container>

            <div class="t-space--small"></div>
            <div class="l-cell-spread-x">
              <data-box
                [imageFull]="row[imageProp]"
                [data]="row"
                [displayFilter]="props"
                [isWrapped]="false"
                (onImgClicked)="rowClickCallbackWrapper(row, $event)"
              ></data-box>
            </div>
          </div>
        </li>
      </ul>
    </ng-template>

    <ng-template #checkbox let-row="row">
      <ng-container *ngIf="!readOnly">
        <span class="ui-cell" *ngIf="checkList">
          <checkbox-wrap>
            <input
              type="checkbox"
              [ngClass]="{ 'is-asRadio': selectOne }"
              [checked]="testCheck(row) || testPreselected(row)"
              (change)="listItemToggle($event, row)"
              [disabled]="testDisabled(row) || testPreselected(row)"
            />
          </checkbox-wrap>
        </span>

        <span *ngIf="!removeUnselect && !checkList" class="ui-cell">
          <button
            class="btn-unselect"
            (click)="unSelect(row, $event)"
            [attr.aria-label]="
              'srpstoreapp.general.cancelSelection' | translate
            "
          ></button>
        </span>
      </ng-container>
    </ng-template>

    <ng-template #image let-cell="cell">
      <figure class="cell-100 t-frame--100">
        <img [attr.src]="cell" alt="" />
      </figure>
    </ng-template>

    <ng-template #paginationListTemplate>
      <!-- <pagination
        [rows]="rows"
        [pagination]="pagination"
        (onPagination)="updateDisplayRowsOnPagination($event)"
      ></pagination> -->
    </ng-template>
  `,
})
export class CheckboxListComponent implements OnInit, OnDestroy {
  @Input("rows") set rw(rows: any[]) {
    if (!rows) return;
    this.rows = rows;
    this.displayRows = this.rows;
  }
  @Input() selected: any[] = [];
  @Input() props: string[];
  @Input() idType: string;
  @Input() title: string;
  @Input() onDataRender: Function;
  @Input() displayAs: "list" | "buttons" | "cards" = "list";
  @Input() removeUnselect: boolean;
  @Input() checkList: boolean = false;
  @Input() emitArray: boolean = true;
  @Input() isUnWrapped: boolean;
  @Input() readOnly: boolean;
  @Input() imageProp: string;
  @Input() afterTitele: TemplateRef<any>;
  @Input() beforeItem: TemplateRef<any>;
  @Input() rowClickCallback: Function;
  @Input() pagination: number;
  @Input() paginationTop: boolean;
  @Input() selectOne: boolean;
  @Input() rowDisabledTest: (row) => boolean;
  @Input() rowPreselectedTest: (row) => boolean;
  @Output() onListUpdate = new EventEmitter<any>();
  @Output() onItemCheckboxChange = new EventEmitter();
  @Output() onRowUnselect = new EventEmitter();
  @ViewChild("clickableListItems", { static: false })
  clickableListItems: ElementRef;
  @ViewChildren("rows") listItemsHTML: QueryList<any>;

  rows: any[];
  displayRows: any[];
  keyCodes = new KeyCodes();

  constructor(private uiService: UiService) {}

  ngOnInit() {
    if (this.onDataRender) this.onDataRender(this.rows);
    if (this.props) this.props = this.props.filter((i) => i !== this.idType);
    this.displayRows = this.rows;
  }

  ngAfterViewInit() {
    this.clickOnEnter();
  }

  ngOnDestroy() {
    document.removeEventListener("keyup", this.enterKeyupHandler);
  }

  public isImage(str: string) {
    return this.uiService.isImage(str);
  }

  public unSelect(row, $event: MouseEvent) {
    $event.stopPropagation();
    this.rows.splice(
      this.rows.findIndex((e) => e === row),
      1
    );
    this.onListUpdate.emit(this.rows);
    this.onRowUnselect.emit(row);
  }

  public listItemToggle($event, row) {
    $event.stopPropagation();
    const checked = $event.target.checked;
    this.updateSelection(checked, row);
    if (this.emitArray) {
      this.onItemCheckboxChange.emit(this.selected);
    } else {
      this.onItemCheckboxChange.emit({
        checked: checked,
        row: row,
      });
    }
  }

  public testCheck(row) {
    if (!this.selected) return;
    if (this.idType) {
      return !!this.selected.find((e) => e[this.idType] === row[this.idType]);
    } else {
      return !!this.selected.find((e) => e === row);
    }
  }

  public rowClickCallbackWrapper(row, $event: MouseEvent) {
    if (!this.rowClickCallback) return;
    this.rowClickCallback(row);
    this.listItemsHTML.forEach((item) => {
      const classAction: string =
        item.nativeElement === $event.currentTarget ? "add" : "remove";
      item.nativeElement.classList[classAction]("is-clicked");
    });
  }

  private updateSelection(checked: boolean, row) {
    if (!this.selected) return;
    if (this.selectOne) {
      this.selected.splice(0, this.selected.length);
      this.selected.push(row);
      return;
    }
    if (checked) {
      this.selected.push(row);
    } else {
      this.selected.splice(
        this.selected.findIndex((e) => e === row),
        1
      );
    }
  }

  private enterKeyupHandler = ($event: KeyboardEvent) => {
    switch ($event.keyCode) {
      case this.keyCodes.enter:
        const focusElement = document.activeElement;
        if (
          focusElement.classList.contains("t-addRemove-item") &&
          focusElement.getAttribute("role") === "button"
        ) {
          focusElement["click"]();
        }
    }
  };

  private clickOnEnter() {
    if (!this.rowClickCallback) return;
    document.addEventListener("keyup", this.enterKeyupHandler);
    //this.uiService.arrowNav( this.clickableListItems.nativeElement );
  }

  updateDisplayRowsOnPagination($event) {
    this.displayRows = $event;
  }

  testDisabled(row): boolean {
    if (this.rowDisabledTest) return this.rowDisabledTest(row);
    else return false;
  }

  testPreselected(row): boolean {
    if (this.rowPreselectedTest != null) return this.rowPreselectedTest(row);
    else return false;
  }
}
